/**
 * Holidays management page.
 * List with {@link SlidePanel} for create/edit forms.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { CalendarSyncModal } from '@/components/calendar/CalendarSyncModal';
import { CALENDAR_SYNC_ENABLED } from '@/config/features';
import { Button } from '@/components/forms/Button';
import { RefreshCw } from 'lucide-react';
import { SlidePanel } from '@/components/common/SlidePanel';
import { HolidaysDataTable } from '@/components/holidays/HolidaysDataTable';
import { CreateHolidayView } from '@/components/holidays/CreateHolidayView';
import { EditHolidayView } from '@/components/holidays/EditHolidayView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import {
  DEFAULT_HOLIDAY_FILTERS,
  type HolidayFilters,
} from '@/components/holidays/holiday-configs';

/** Header export + optional create button. */
function renderHolidayPageActions(
  canCreate: boolean,
  onCreate: () => void,
  onOpenSync: () => void,
): React.ReactNode {
  return (
    <div className="flex items-center gap-3">
      {CALENDAR_SYNC_ENABLED ? (
        <Button preset="export" icon={RefreshCw} onClick={onOpenSync}>
          Synchroniser
        </Button>
      ) : null}
      {canCreate ? (
        <Button preset="create-entity" onClick={onCreate}>
          Créer une période
        </Button>
      ) : null}
    </div>
  );
}

/** Holidays list with {@link SlidePanel} create/edit. */
export const HolidaysManagementPage: React.FC = () => {
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('holidays');
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editHolidayId, setEditHolidayId] = React.useState<string | null>(null);
  const { openRowId, openRow, setOpenRow, closeRow } = useDetailsView();
  const { viewMode, setViewMode } = useViewMode('holidays-management');
  const [holidayFilters, setHolidayFilters] =
    React.useState<HolidayFilters>(DEFAULT_HOLIDAY_FILTERS);
  const [syncOpen, setSyncOpen] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  function handleOpenSync(): void {
    setSyncOpen(true);
  }

  function handleCloseSync(): void {
    setSyncOpen(false);
  }

  /** Deep-link: open create/edit/view via `?create=` / `?edit=` / `?view=`. */
  React.useEffect(() => {
    const editId = searchParams.get('edit');
    const viewId = searchParams.get('view');
    const shouldCreate = searchParams.get('create') === 'true';

    if (editId) {
      setEditHolidayId(editId);
      closeRow();
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }

    if (viewId) {
      setOpenRow(viewId);
      setCreateFormOpen(false);
      setEditHolidayId(null);
    }

    if (shouldCreate) {
      setCreateFormOpen(true);
      setEditHolidayId(null);
      closeRow();
    }
  }, [searchParams, setSearchParams, closeRow, openRow]);

  /** Open the edit panel and close any expanded details row. */
  const handleEditHoliday = (id: string) => {
    setEditHolidayId(id);
    closeRow();
  };

  /** Scroll the page to the form when create or edit first opens. */
  const prevCreateFormRef = React.useRef(createFormOpen);
  const prevEditIdRef = React.useRef(editHolidayId);
  React.useEffect(() => {
    const formStateChanged =
      prevCreateFormRef.current !== createFormOpen || prevEditIdRef.current !== editHolidayId;
    if (formStateChanged && (createFormOpen || editHolidayId !== null) && contentRef.current) {
      contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    prevCreateFormRef.current = createFormOpen;
    prevEditIdRef.current = editHolidayId;
  }, [createFormOpen, editHolidayId]);

  /** Open the create panel and close any expanded row. */
  function handleOpenCreate(): void {
    setCreateFormOpen(true);
    setEditHolidayId(null);
    closeRow();
  }

  return (
    <ManagementPage
      title="Gestion des Vacances et Jours Fériés"
      subtitle="Créez, modifiez et gérez toutes vos vacances et jours fériés"
      contentRef={contentRef}
      actions={renderHolidayPageActions(canCreate, handleOpenCreate, handleOpenSync)}
    >
      <HolidaysDataTable
        expandedRowId={openRowId}
        onToggleRow={openRow}
        onCloseDetails={closeRow}
        onEditHoliday={handleEditHoliday}
        enableFilters={true}
        onFiltersChange={setHolidayFilters}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={() => setCreateFormOpen(false)}>
        <CreateHolidayView
          onClose={() => setCreateFormOpen(false)}
          onSuccess={() => {
            setCreateFormOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['holidays'] });
          }}
        />
      </SlidePanel>

      <SlidePanel isOpen={editHolidayId !== null} onClose={() => setEditHolidayId(null)}>
        {editHolidayId && (
          <EditHolidayView
            key={editHolidayId}
            holidayId={editHolidayId}
            onClose={() => setEditHolidayId(null)}
            onSuccess={() => {
              setEditHolidayId(null);
              void queryClient.invalidateQueries({ queryKey: ['holidays'] });
            }}
          />
        )}
      </SlidePanel>
      {CALENDAR_SYNC_ENABLED ? (
        <CalendarSyncModal
          open={syncOpen}
          onClose={handleCloseSync}
          lockedTypes={['holiday']}
          filterSeed={{ holidayFilters }}
        />
      ) : null}
    </ManagementPage>
  );
};
